import type { Immutable } from "immer";
import React from "react";

import type { AwardFeatureBlock as AwardFeatureBlockValue } from "@reactivated";

import { RichTextBlock } from "@thelabnyc/thelabui/src/components/RichText";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

type Value = AwardFeatureBlockValue;
type Award = NonNullable<Value["award"]>;
type AwardImage = NonNullable<Award["award_image"]>;

/**
 * Faithful port of the legacy server-rendered
 * ``cms/blocks/award-feature-block.html``.
 *
 * Image: the legacy ``{% wagtail_image value.award.award_image
 * specs='height-150' %}`` expands (per ``lazy-wagtail-image.html``) to a plain
 * eager ``<img>`` under the puppeteer UA, with ``src`` = the single
 * ``height-150`` rendition and ``alt`` = the image TITLE (the template hardcodes
 * ``alt="{{ original.title }}"``). The legacy ``alt="Find out more about {{
 * award_image.get_title }}"`` tag argument is a no-op for the alt attribute (the
 * template never echoes ``attrs.alt`` over the hardcoded ``original.title``, and
 * ``award_image`` is not in the inclusion-tag context anyway), so the effective
 * rendered ``alt`` is the image title -- matching the AwardColumnsBlock port. The
 * ``height-150`` rendition is serialized via ``extra_client_data_renditions`` on
 * the shared ``AwardSubBlock`` image field. The production-only lazysizes
 * plumbing (placeholder data-URI + ``<noscript>``) is not reproduced, matching
 * the AwardColumnsBlock / FeaturesBlock port convention.
 *
 * Link: ``award_link`` is the ``tsicommon_cms.Link`` snippet; the legacy
 * ``{% if award_link.link_external %}`` external-target check maps to the
 * serialized ``link_external`` field.
 *
 * Content: legacy ``{% include_block value.award.content %}`` emits the
 * ``.rich-text``-wrapped HTML directly under ``<section>``.
 */
const renderAwardImage = (
    image: Immutable<AwardImage> | null | undefined,
): React.JSX.Element | null => {
    if (!image) {
        return null;
    }
    return (
        <img
            alt={image.title}
            src={image.renditions["height-150"]?.url ?? image.url}
            loading="lazy"
            decoding="async"
        />
    );
};

export const AwardFeatureBlock = (
    props: Immutable<{ value: AwardFeatureBlockValue }>,
): React.JSX.Element => {
    const v = props.value;
    const award = v.award;
    const link = award?.award_link;
    const img = renderAwardImage(award?.award_image);
    return (
        <section
            className={concatClassNames([
                "award-featured-block",
                v.background_style?.style ?? "",
                v.responsive_visibility?.visibility ?? "",
            ])}
        >
            {link ? (
                <a
                    href={link.url}
                    target={link.link_external ? "_blank" : undefined}
                    rel={link.link_external ? "noopener noreferrer" : undefined}
                >
                    {img}
                </a>
            ) : (
                img
            )}
            {/* No `.rich-text` className: legacy `{% include_block value.award.content %}`
                of the raw RichText value emits bare HTML (no `.rich-text`), so the
                award-feature SCSS `& .rich-text { text-align: center }` rule was dead.
                Adding the class activated it (text-centering + spacing shift). Matches
                the proven AwardColumnsBlock port, which renders content classless. */}
            <RichTextBlock value={award?.content ?? ""} />
        </section>
    );
};
